<template>
  <div id="default-layout" :style="style">
    <v-card width="400" class="mx-auto px-10 py-8" elevation="2">
      <v-img :src="logo" alt="Maropost logo" height="60" ></v-img>
      <div class="my-6">
        <h4 class="font-weight-large">To continue, Choose an account</h4>
      </div>
      <v-form v-model="isFormValid" @submit.prevent>
        <v-autocomplete
          ref="autocompleteRef"
          v-model:search="search"
          v-model="model"
          :items="items"
          label="Select an account"
          item-value="id"
          item-title="name"
          :append-outer-icon="isLoading ? 'mdi-loading' : ''"
          :loading="isLoading"
          :no-data-text="'No record found'"
          v-bind="$attrs"
          :rules="[required('Account')]"
          return-object
          @update:focused="onFocusHandler"
          @update:search="debouncedSearchHandler"
          @update:model-value="onModelUpdate"
        >
          <template #prepend-item>
            <v-list-item v-if="isLoading">
              <v-list-item-title>Loading...</v-list-item-title>
            </v-list-item>
          </template>
          <template #append-item>
            <v-list-item v-if="isLoadMoreEnabled" v-intersect="onIntersect">
              <v-list-item-title>Loading more...</v-list-item-title>
            </v-list-item>
          </template>
          <template #item="{ props, item }">
            <v-list-item v-bind="props">
              <template #prepend>
                <v-icon v-if="item.raw.id === accountId" size="24" class="mr-2" style="color: #f44336 !important;">
                  mdi-alert-circle-outline
                </v-icon>
              </template>
            </v-list-item>
          </template>
  
        </v-autocomplete>
        <v-btn
          :variant="isFormValid ? 'flat' : 'outlined'"
          density="default"
          class="text-body-2 text-uppercase mt-1 font-weight-bold w-100"
          base-color="black"
          :disabled="!isFormValid"
          @click="onSubmitHandler"
        >
        Submit
      </v-btn>
      <div class="body-2 pt-4 text-medium-emphasis">If the problem still persists 
        <a href="mailto:support@maropost.com?subject=Support" target="_blank">Contact Support</a>
      </div>
      </v-form>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import logo from '@/assets/maropost-logo.svg'
import type { VAutocomplete } from 'vuetify/components'
import { Account, useAccountService } from '@/lib/services'
// @ts-ignore - lodash types not available
import { debounce } from 'lodash'
import { useRoute } from 'vue-router'
import background from '@/assets/images/background.jpeg'

const route = useRoute()

const style = {
  backgroundImage: `url('${background}')`
}

// Simple validation function
const required = (fieldName: string) => {
  return (value: Account | null) => {
    if (!value) {
      return `${fieldName} is required`
    }
    return true
  }
}

const autocompleteRef = ref<InstanceType<typeof VAutocomplete> | null>(null)
const model = ref<Account | null>(null)
const $account = useAccountService()
const isFormValid = ref(false)
const isLoading = ref(false)
const items = ref<Account[]>([])
const hasItems = ref(false)
const search = ref('')
const page = ref(1)
const itemsPerPage = 10
const selecting = ref(false)
const accountId = ref(0)

onMounted(() => {
  if (route.query.accountId) {
    accountId.value = Number(route.query.accountId)
  }
})

const onModelUpdate = () => {
  selecting.value = true

  // reset after next tick or short delay
  setTimeout(() => {
    selecting.value = false
  }, 600)
}

/**
 * Triggered when the intersection observer detects the end of the item list.
 * Loads more items if the observer indicates the component is intersecting.
 *
 * @param {boolean} isIntersecting - Indicates if the component is currently intersecting.
 */
const onIntersect = (isIntersecting: boolean) => {
  if (isIntersecting && !$account.isFetching.value && hasItems.value) {
    loadMoreItems()
  }
}

const isLoadMoreEnabled = computed(() => {
  return (
    hasItems.value &&
    !$account?.isFetching?.value 
  )
})

/**
 * Loads more items if available and not already loading.
 */
const loadMoreItems = async () => {
  if (
    !hasItems.value ||
    $account.isFetching.value 
  )
    return // Prevent multiple requests
  page.value++
  await fetchItems()
}

/**
 * Fetches items from the API using the provided service.
 * Constructs the request parameters based on current state.
 */
const fetchItems = async () => {
  const searchKeyword = items.value.find((item) => {
      return item['name'] === search.value
    })? ''
      : search.value

  const config = {
    abort: true,
    params: {
      per_page: itemsPerPage, // Set limit for items per page
      page: page.value,
      name_cont: searchKeyword,
      sort_by: 'name',
      sort_order: 'asc'
    }
  }

  try {
    isLoading.value = true
    await $account.findAll(config)
    const data = ($account.items.value || []) as Account[]
    items.value = page.value == 1 ? [...data] : [...items.value, ...data]
    if (search.value && autocompleteRef.value) {
      autocompleteRef.value.menu = true
    }
    hasItems.value = data.length >= itemsPerPage
  } catch (error: unknown) {
    console.error('Error fetching items:', error)
  } finally {
    isLoading.value = false
  }
}
function onFocusHandler(isFocused: boolean) {
  isFocused && items.value.length < 10 && !$account.isFetching.value && fetchItems()
}
/**
 * Triggered when the search input changes. Resets the page to 1 and fetches items.
 */
const onSearchHandler = () => {
  if (!selecting.value) {
    page.value = 1
    hasItems.value = false
    fetchItems()
  }
}

// Debounced version of the search handler
const debouncedSearchHandler = debounce(onSearchHandler, 500) // Adjust delay as needed


function onSubmitHandler() {
  if (!model.value) return
  
  const accountId = model.value.id
  window.location.href = `/accounts/${accountId}/dashboard`
}
</script>
<style scoped lang="scss">
 #default-layout {
  display: flex;
  justify-content: center; /* Center horizontally */
  align-items: center;     /* Center vertically */
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  min-height: 100vh;
}
.pointer-not-allowed {
  cursor: not-allowed;
}
.select-account-list {
  max-height: 500px;
}
:deep(.v-skeleton-loader__bone.v-skeleton-loader__list-item .v-skeleton-loader__text) {
  height: 3rem !important;
}
:deep(.v-skeleton-loader__bone.v-skeleton-loader__list-item) {
  margin: 0.25rem !important;
}
</style>